import { ARTIFACT_TYPE_CONFIG } from '@bower/shared/constants/artifact-types.generated';
import { TOP_LEVEL_PARENT_ENTITY_TYPES } from '@bower/shared/constants/parent-validity';
import type { ArtifactType, ParentEntityType } from '@bower/shared';
import { bowerGlyph, materialGlyph, type GlyphRef } from '../theme/glyph';

/**
 * The single definition of "things a user can create".
 *
 * Consumed by the one Create modal (its type grid and destination checks).
 * Adding a creatable type means adding one entry here.
 *
 * Scope filtering is derived from `validParentTypes` in
 * `packages/shared/src/schema/artifact-types/` rather than hardcoded, so a new
 * artifact type is offered only where its own registry entry permits.
 */

/** How a create action is fulfilled. */
export type CreateActionKind =
  /** Creates an artifact directly and navigates to it. */
  | { kind: 'artifact'; artifactType: ArtifactType; captureMethod: NoteCaptureMethod }
  /** Opens the Create modal's New collection step. */
  | { kind: 'collection' }
  /** Opens the Create modal's Import step. */
  | { kind: 'import' }
  /** Opens the Create modal's Equipment step. */
  | { kind: 'equipment' }
  /** Picks an .xlsx or .csv file and imports it as a live spreadsheet. */
  | { kind: 'spreadsheet' }
  /** Opens the link form: a shortcut to an outside page. */
  | { kind: 'link' };

/** Capture method passed to the note detail page to auto-start its capture UI. */
export type NoteCaptureMethod = 'text' | 'voice' | 'image_to_text';

export interface CreateActionDefinition {
  /** Stable id — also the analytics `create_type` and the item's test id. */
  readonly id: string;
  /** User-facing label. One string, used on every surface (sentence case). */
  readonly label: string;
  /**
   * The glyph, and the library that holds it (`theme/glyph.ts`). Bower's own
   * set has no link artwork, so `link` takes a Material glyph, the same way
   * ENTITY_ICONS.equipment does.
   */
  readonly icon: GlyphRef;
  readonly action: CreateActionKind;
  /** One line under the label on the create modal's tiles. */
  readonly description: string;
  /** The create modal's single-letter shortcut (desktop, step 1 only). */
  readonly shortcut: string;
}

/**
 * The one icon for Image to text, on this Create tile and on the note top
 * bar's door alike. Bower's own `photo` glyph, the one every image file
 * renders with: the Create set is Bower glyphs only, and Bower has no
 * separate "image" glyph.
 */
export const IMAGE_TO_TEXT_GLYPH: GlyphRef = bowerGlyph('photo');

/**
 * Ordered as the Create modal shows them.
 *
 * Labels are deliberately one string everywhere they appear: calling one
 * thing different names on different screens is how a UI stops feeling like
 * one product.
 */
export const CREATE_ACTIONS: ReadonlyArray<CreateActionDefinition> = [
  {
    id: 'voice',
    label: 'Voice note',
    icon: bowerGlyph('voice'),
    description: 'Record and transcribe',
    shortcut: 'V',
    action: { kind: 'artifact', artifactType: 'note', captureMethod: 'voice' },
  },
  {
    id: 'photo',
    label: 'Image to text',
    icon: IMAGE_TO_TEXT_GLYPH,
    description: 'Turn an image into a note',
    shortcut: 'P',
    action: { kind: 'artifact', artifactType: 'note', captureMethod: 'image_to_text' },
  },
  {
    id: 'import',
    label: 'Import files',
    icon: bowerGlyph('import'),
    description: 'PDFs, images, docs and more',
    shortcut: 'I',
    action: { kind: 'import' },
  },
  {
    id: 'note',
    label: 'Text note',
    // The SAME glyph a note artifact renders with (ENTITY_ICONS.note). The
    // create menu used a generic plus, so the thing you were about to make
    // looked nothing like the thing you got.
    icon: bowerGlyph('note'),
    description: 'Start writing',
    shortcut: 'T',
    action: { kind: 'artifact', artifactType: 'note', captureMethod: 'text' },
  },
  {
    id: 'protocol',
    label: 'Protocol',
    icon: bowerGlyph('protocol'),
    description: 'From a document or from scratch',
    shortcut: 'R',
    action: { kind: 'artifact', artifactType: 'protocol', captureMethod: 'text' },
  },
  {
    id: 'spreadsheet',
    label: 'Spreadsheet',
    // The entity glyph, so the item looks like the thing it makes, the same
    // rule the note item follows.
    icon: bowerGlyph('spreadsheet'),
    description: 'From a CSV or XLSX, or blank',
    shortcut: 'S',
    action: { kind: 'spreadsheet' },
  },
  {
    id: 'equipment',
    label: 'Equipment',
    icon: bowerGlyph('equipment'),
    description: 'Add an instrument',
    shortcut: 'E',
    action: { kind: 'equipment' },
  },
  {
    id: 'link',
    label: 'Link',
    // Same source as ENTITY_ICONS.link, so the item looks like what it makes.
    icon: materialGlyph(ARTIFACT_TYPE_CONFIG.link.icon),
    description: 'A shortcut to a page outside Bower',
    shortcut: 'L',
    action: { kind: 'link' },
  },
  {
    id: 'collection',
    label: 'Collection',
    icon: bowerGlyph('collection'),
    description: 'A folder for related work',
    shortcut: 'C',
    action: { kind: 'collection' },
  },
];

/**
 * Whether an action can create into the given parent.
 *
 * Artifact-backed actions consult the shared registry's `validParentTypes`, so
 * the answer stays correct when that YAML changes. Collections nest under a
 * workspace or another collection; imports and equipment follow the same rule
 * as the artifacts they produce.
 */
export const isActionValidForParent = (
  definition: CreateActionDefinition,
  parentEntityType: ParentEntityType,
): boolean => {
  switch (definition.action.kind) {
    case 'artifact':
      return ARTIFACT_TYPE_CONFIG[definition.action.artifactType].validParentTypes.includes(
        parentEntityType,
      );
    case 'import':
      // Imports land as attachments.
      return ARTIFACT_TYPE_CONFIG.attachment.validParentTypes.includes(parentEntityType);
    case 'equipment':
      return ARTIFACT_TYPE_CONFIG.equipment.validParentTypes.includes(parentEntityType);
    case 'spreadsheet':
      return ARTIFACT_TYPE_CONFIG.spreadsheet.validParentTypes.includes(parentEntityType);
    case 'link':
      return ARTIFACT_TYPE_CONFIG.link.validParentTypes.includes(parentEntityType);
    case 'collection':
      // Collections nest under a workspace or another collection. Reuses the
      // shared list rather than restating it — this was the one hardcoded
      // matrix in an otherwise fully derived filter.
      return (TOP_LEVEL_PARENT_ENTITY_TYPES as ReadonlyArray<ParentEntityType>).includes(
        parentEntityType,
      );
    default:
      return false;
  }
};

/** The actions creatable into a given parent, in registry order. */
export const createActionsForParent = (
  parentEntityType: ParentEntityType,
): ReadonlyArray<CreateActionDefinition> =>
  CREATE_ACTIONS.filter((definition) => isActionValidForParent(definition, parentEntityType));
